/**
 * Named grant levels, so comparisons read as vocabulary rather than string
 * literals. A const object rather than a TS `enum` because these values arrive
 * as plain strings on the wire: an enum-typed field would reject
 * `permission_level: "owner"` from an API response or a test fixture, while
 * this keeps assignment from the literal union working.
 */
export const PermissionLevel = {
  Read: "read",
  Write: "write",
  Owner: "owner",
} as const;

export type PermissionLevel =
  (typeof PermissionLevel)[keyof typeof PermissionLevel];

export interface Group {
  id: number;
  name: string;
  description?: string;
  member_count?: number;
  create_date?: string;
  updated_date?: string;
}

export interface GroupMember {
  user_id: number;
  email: string;
  name?: string;
}

export interface GroupWithMembers extends Group {
  members: GroupMember[];
}

export interface GroupListResponse {
  items: Group[];
  // Table count, not page size — items is capped at the server's 500
  // hard limit, so total > items.length means the page is truncated.
  total: number;
}

export interface GroupMembersResponse {
  items: GroupMember[];
  // Total matching members (reflects the optional search filter) — one page
  // is one slice. Drives the members DataGrid's server-side rowCount.
  total: number;
}

export interface GroupFolderGrant {
  folder_id: number;
  folder_name: string;
  tier: PermissionLevel;
  recursive: boolean;
  // A grant on a soft-deleted folder is still a real row: revoked on
  // group delete, live again on folder restore.
  folder_deleted: boolean;
}

export interface GroupFolderGrantsResponse {
  items: GroupFolderGrant[];
  total: number;
}

export interface ProjectFolder {
  id: number;
  name: string;
  parent_id?: number;
  owner_id: number;
  owner?: { id: number; email: string; name?: string };
  permission_level?: PermissionLevel;
  is_traversal_only?: boolean;
  // Number of visible direct children; lets the FE hide the expand chevron
  // on leaf folders without a per-node useFolders() probe.
  child_count?: number;
  // Number of folder-scoped files; with child_count, lets the tree hide the
  // chevron on a truly empty folder instead of expanding to nothing.
  file_count?: number;
  create_date?: string;
  updated_date?: string;
}

/**
 * Whether a folder is not the viewer's own — someone else's grant, or a
 * permission the payload never resolved.
 *
 * `permission_level` is absent on the folder listing exactly for
 * traversal-only ancestors: the router sets
 * `is_traversal_only = f.id not in levels` alongside `levels.get(f.id)`
 * (`mre/routers/project_folders.py`), so no level means no grant on that
 * folder — the viewer sees it only as a path to something readable below.
 * Unknown therefore reads as **not owned**, which is also what the capability
 * checks conclude, and they fail closed.
 *
 * Not the forest search, despite what an earlier version of this comment
 * said: that endpoint builds `permission_by_id` from the same
 * `read_accessible_folder_ids` list the hits come from, so every hit resolves
 * (see the note on `FolderSearchHit.permission_level` below).
 *
 * Use this for anything that *presents* ownership: the pane's group split, the
 * ownership filters, the folder icon.
 *
 * Do NOT use it for a capability check. "May I rename this" is a different
 * question with a different shape — `=== "owner"`, or `owner || write` for
 * move and upload — and those must stay explicit so undefined denies rather
 * than being folded into a boolean about sharing. `FolderActionsMenu` is the
 * reference for that side.
 *
 * Four callers still decide ownership without this predicate, all pre-existing
 * on `dev` and outside this PR's diff: `FolderContentsList`'s row badge guards
 * with `permission_level && … !== "owner"` (reading undefined as owned);
 * `LibraryPane` and `MoveDialog` hardcode the owned glyph on search rows; and
 * `FolderTreeItem` colors its glyph from the inherited `isShared` prop rather
 * than the folder. Converting them is a follow-up.
 */
export const isSharedFolder = (folder: {
  permission_level?: PermissionLevel;
}): boolean => folder.permission_level !== PermissionLevel.Owner;

/**
 * Who may open the share dialog: the owner alone, since sharing grants access
 * the sharer must itself be able to delegate.
 *
 * Shared by the two surfaces that offer the action — the browse toolbar button
 * and FolderActionsMenu's item — so the two cannot drift. They previously
 * spelled the same rule two ways (`PermissionLevel.Owner` against the raw
 * `"owner"` literal).
 */
export const canShareFolder = (
  folder: { permission_level?: PermissionLevel } | undefined | null,
): boolean => folder?.permission_level === PermissionLevel.Owner;

export interface UserShareInfo {
  user_id: number;
  email: string;
  name?: string;
  permission_level: PermissionLevel;
  // One grant row per subject: recursive=true means the grant reaches the
  // whole subtree (computed at read time, spec §5.3 — not a fan-out).
  recursive: boolean;
  granted_at?: string;
}

export interface GroupShareInfo {
  group_id: number;
  group_name: string;
  // Groups are capped at "write" by the backend (own is user-only).
  permission_level: Exclude<PermissionLevel, "owner">;
  recursive: boolean;
  granted_at?: string;
}

export interface FolderSharesResponse {
  folder_id: number;
  user_shares: UserShareInfo[];
  group_shares: GroupShareInfo[];
}

export interface ProjectFolderAncestor {
  id: number;
  name: string;
  // The caller's own effective level on this ancestor; undefined means none
  // (same "unknown reads as not owned" rule as ProjectFolder.permission_level
  // above). Lets a client skip a doomed owner-gated /shares call instead of
  // firing it and reading the 403 back (MRE#30776).
  permission_level?: PermissionLevel;
}

export interface ProjectFile {
  name: string;
  path: string;
  size_bytes: number;
  content_type?: string;
  last_modified?: string;
  // Index/ingest state, derived from the file's SDR (null if no SDR row).
  // Populated by list_files (6.6.12-backend); drives the viewer status chip.
  sdr_id?: number | null;
  artifact_id?: number | null;
  processing_status?: string | null;
  /**
   * Display title, derived server-side (MRE#30875): the extracted document
   * title, else the stored file name. Absent against a backend without it,
   * and for a file whose record does not exist yet — callers fall back to
   * `name`.
   *
   * `name` stays the storage filename. It keys the row and its actions, so
   * the label and the key are deliberately different fields.
   */
  title?: string | null;
  /**
   * Revision-chain fields (Library §2.1), matching `RevisionInfo`'s naming.
   * Absent/null for a pre-chain file or one with no SDR yet. `list_files`
   * excludes a superseded row by default, so `superseded_date` is normally
   * null here — it's carried anyway for the rare `include_superseded=true`
   * request (a future per-file revision picker).
   */
  chain_id?: number | null;
  revision_label?: string | null;
  superseded_date?: string | null;
}

// Mints a root folder. The only root-mint path (spec §5.8, Q4).
export interface CreateProjectRequest {
  name: string;
}

// Creates a subfolder. parent_id is required: POST /project-folders/ 422s
// without it; roots come from the create-project path.
export interface CreateFolderRequest {
  name: string;
  parent_id: number;
}

export interface UpdateFolderRequest {
  name?: string;
  // Set to move the folder; omitted leaves the parent alone. Move-to-root
  // is not expressible (spec §5.8, Q4).
  parent_id?: number;
}

export interface AddShareRequest {
  user_id?: number;
  group_id?: number;
  permission_level: PermissionLevel;
  recursive?: boolean;
}

// Admin-only repoint of a folder's owner grant (spec §5.8, Q2).
// previous_owner_user_id defaults to the folder's display owner.
export interface TransferOwnershipRequest {
  new_owner_user_id: number;
  previous_owner_user_id?: number;
}

export type AddFromWorkspaceSource =
  | "agent_workspace"
  | "dialogue_adhoc"
  | "workspace_file";

// dialogue_id is required for the two dialogue-coupled sources; the
// workspace-tab source carries workspace_path instead (own-prefix
// containment is the authorization, spec §5.9).
export interface AddFromWorkspaceRequest {
  source: AddFromWorkspaceSource;
  dialogue_id?: number;
  artifact_id?: number;
  sdr_id?: number;
  workspace_path?: string;
  // Replace an existing same-named file in place instead of deconflicting
  // to "name (N)". The backend 409s if no file with that name exists.
  overwrite?: boolean;
}

export interface MoveFileRequest {
  destination_folder_id: number;
}

// === ACCESS-GAP DISCLOSURE (pre-share, Story 13 AC 5/6) ===
// Mirrors mre/schema/project_folder_schema.py AccessGapRequest/Response.

export interface AccessGapRequest {
  user_ids: number[];
  group_ids: number[];
  // Scope of the share being previewed; the backend defaults it to true.
  recursive?: boolean;
}

// A referenced record a recipient cannot open, named to the sharer (the
// deliberate §4.5 carve-out — the sharer chose the recipients and records).
export interface BlockedRecord {
  sdr_id: number;
  title: string;
  container_type: "collection" | "project_folder";
  container_id: number;
  container_name?: string | null;
}

export interface MemberAccessGap {
  user_id: number;
  email: string;
  name?: string | null;
  // Requested groups this user arrived through; empty for direct user_ids.
  via_group_ids: number[];
  readable_count: number;
  unreadable_count: number;
  unreadable: BlockedRecord[];
}

export interface GroupAccessGap {
  group_id: number;
  name: string;
  member_count: number;
  members_evaluated: number;
  members_fully_covered: number;
  members_with_gaps: number;
}

// Explicit bounds applied to the computation (§22.6: a stated limit shown to
// the user, never a silent truncation).
export interface AccessGapLimits {
  member_cap: number;
  reference_cap: number;
  members_truncated: boolean;
  references_truncated: boolean;
}

export interface AccessGapResponse {
  folder_id: number;
  records_total: number;
  // Homed in the subtree, covered by the share itself — readable by every
  // recipient. Only referenced_records vary per recipient.
  uploaded_records: number;
  referenced_records: number;
  members: MemberAccessGap[];
  groups: GroupAccessGap[];
  unknown_user_ids: number[];
  unknown_group_ids: number[];
  limits_applied: AccessGapLimits;
}

export interface FileUploadResponse {
  name: string;
  path: string;
  size_bytes: number;
  deconflicted?: boolean;
  original_name?: string;
  // SDR minted by folder ingestion; null when ingestion dispatch failed
  // (plain uploads still 201 in that case, publishes 500 instead).
  sdr_id?: number | null;
}

export interface FolderSearchHit {
  kind: "file" | "folder";
  folder_id: number;
  folder_name: string;
  relative_path: string;
  filename?: string | null;
  sdr_id?: number | null;
  snippets?: string[];
  // The user's permission on the hit's folder, when the caller resolves it
  // (the forest search does). Lets a destination picker gate writability.
  permission_level?: ProjectFolder["permission_level"];
  score: number;
}

export interface FolderSearchResponse {
  items: FolderSearchHit[];
  total: number;
}

// === SSE EVENTS ===

// Action values published on notify:folder_permissions:changed (the
// publish sites in mre/crud/project_folder_crud.py and group_crud.py).
// The backend may grow new actions. Consumers bust caches on every event
// instead of switching on this field, so an unlisted value degrades to a
// generic "permissions changed".
export type FolderPermissionChangeAction =
  | "share_added"
  | "share_updated"
  | "share_removed"
  | "folder_deleted"
  | "folder_restored"
  | "folder_moved"
  | "member_added"
  | "member_removed"
  | "group_deleted";

// Mirrors mre/schema/notification_events.py FolderPermissionChangedEvent.
// folder_id is the back-compat anchor; affected_folder_ids carries the full
// fan-out (the subtree of a recursive grant, every folder granted to a
// group). affected_user_ids gates delivery server-side; null = broadcast.
export interface FolderPermissionChangedEvent {
  folder_id: number;
  action: FolderPermissionChangeAction;
  affected_folder_ids: number[];
  affected_user_ids?: number[] | null;
}

// One file in the agent-visible forest snapshot (spec §5.10 context chip).
// relative_path is forest-rooted: "Root/Sub/file.ext".
export interface ContextSnapshotFile {
  relative_path: string;
  size_bytes?: number | null;
}

// The caller's agent-visible project file set. Mirrors the FUSE mount /
// context injector (the whole readable forest, not one folder's subtree).
// files is capped at `cap` entries; `total` counts the full set.
export interface ContextSnapshotResponse {
  files: ContextSnapshotFile[];
  total: number;
  truncated: boolean;
  cap: number;
}

// Per-file processing/index state, keyed by filename. Mirrors
// mre/schema/project_folder_schema.py ProjectFileProcessingStatus and the
// dataset /processing-status item shape so DocumentStatusIndicator consumes
// it unchanged.
export interface ProjectFileProcessingStatus {
  name: string;
  sdr_id: number;
  status: string | null;
  display_status: string | null;
  is_processing: boolean;
  progress_percentage: number;
  error_message: string | null;
  num_chunks: number | null;
  num_chunks_ready: number | null;
  is_retryable: boolean | null;
}

// Folder-level rollup for the toolbar chip.
export interface FolderProcessingSummary {
  total: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface FolderProcessingStatusResponse {
  folder_id: number;
  files: ProjectFileProcessingStatus[];
  summary: FolderProcessingSummary;
}
